import React, { useRef, useState } from "react"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@material-ui/core"
import Autocomplete from "@material-ui/lab/Autocomplete"
import GavelIcon from "@material-ui/icons/Gavel"
import { useNotify } from "react-admin"

import { db } from "../../firebase"
import getHttpsCallable from "../../utils/getHttpsCallable"
import casinodata from "../../constants/casinos.json"

type ProviderData = Record<string, any>

// Map casino name -> operator ("Betreiber"), mirroring the admin edit lookup
// (casinodata.find(o => o["Name des Casinos"] === value)?.Betreiber).
const OPERATOR_BY_CASINO: Record<string, string> = (
  casinodata as Array<Record<string, string>>
).reduce((acc, row) => {
  const key = (row["Name des Casinos"] ?? "")
    .trim()
    .toLowerCase()
  if (key) {
    acc[key] = row.Betreiber ?? ""
  }
  return acc
}, {} as Record<string, string>)

function lookupOperator (casinoName?: string): string {
  if (!casinoName) {
    return ""
  }
  const key = casinoName
    .trim()
    .toLowerCase()
  return OPERATOR_BY_CASINO[key] ?? ""
}

// Casino names for the game-name dropdown (mirrors the edit form's choices).
const CASINO_OPTIONS: string[] = Array.from(
  new Set(
    (casinodata as Array<Record<string, string>>)
      .map((row) => row["Name des Casinos"])
      .filter(Boolean),
  ),
).sort()

type S16bValues = {
  s16bGameName: string
  // The operator is the canonical provider.company (defendant used by the POA,
  // CMT and file names) — not a §16b-only override.
  company: string
  s16bAccountNumber: string
  s16bTlDate: string
  s16bLossSum: string
}

// Pre-fill each field: persisted §16b override first, else the default the
// contract falls back to (name, casinos.json operator, exact loss).
function defaultsFor (provider: ProviderData): S16bValues {
  return {
    s16bGameName: provider.s16bGameName ?? provider.name ?? "",
    company: provider.company ||
      lookupOperator(provider.name) ||
      "",
    s16bAccountNumber: provider.s16bAccountNumber ?? "",
    s16bTlDate: provider.s16bTlDate ?? "",
    s16bLossSum: String(
      provider.s16bLossSum ||
      provider.calculatedLossesTotal ||
      provider.calculatedLosses ||
      "",
    ),
  }
}

const FIELD_LABELS: Array<{
  key: keyof S16bValues
  label: string
  type?: string
}> = [
  { key: "s16bGameName", label: "Name des Online-Glücksspiels" },
  { key: "company", label: "Name des Glücksspiel-Betreibers" },
  { key: "s16bAccountNumber", label: "Nummer des Accounts" },
  { key: "s16bTlDate", label: "Datum der Transaktionsliste", type: "date" },
  { key: "s16bLossSum", label: "Summe des Verlustes (EUR)" },
]

type Props = {
  userId: string
  claimId: string
  /** The full customFieldValues.providers.value array. */
  providers: ProviderData[]
  /** Indices into `providers` to show; defaults to all providers. */
  indices?: number[]
  /** Whether the POA signature is submitted (required to regenerate). */
  signatureSubmitted: boolean
  /** One-shot "show modal" flag; cleared when the dialog closes. */
  showModal?: boolean
}

export default function Section16bReviewButton (
  { userId, claimId, providers, indices, signatureSubmitted, showModal }: Props,
) {
  const notify = useNotify()
  const shownIndices = indices ?? Array.from(providers.keys())

  function computeDefaults (): Record<number, S16bValues> {
    const init: Record<number, S16bValues> = {}
    for (const i of shownIndices) {
      init[i] = defaultsFor(providers[i] ?? {})
    }
    return init
  }

  const showFromFlag = Boolean(showModal) && shownIndices.length > 0
  const [open, setOpen] = useState(showFromFlag)
  const [saving, setSaving] = useState(false)
  const [values, setValues] = useState<Record<number, S16bValues>>(
    () => (showFromFlag ? computeDefaults() : {}),
  )
  // The show-modal flag is a one-shot trigger: clear it once the dialog is
  // closed (saved or not) so the modal does not reopen on reload.
  const needClearRef = useRef(showFromFlag)

  async function clearShowFlag () {
    if (!needClearRef.current || !userId || !claimId) {
      return
    }
    needClearRef.current = false
    try {
      await db
        .doc(`users/${userId}/claims/${claimId}`)
        .update({ s16bShowModal: false })
    }
    catch {
      // ignore; flag is re-evaluated on next load
    }
  }

  function handleClose () {
    setOpen(false)
    clearShowFlag()
  }

  function openDialog () {
    setValues(computeDefaults())
    setOpen(true)
  }

  function setField (index: number, field: keyof S16bValues, val: string) {
    setValues((prev) => ({
      ...prev,
      [index]: { ...prev[index], [field]: val },
    }))
  }

  function renderField (
    i: number,
    v: S16bValues,
    field: { key: keyof S16bValues, label: string, type?: string },
  ) {
    const { key, label, type } = field
    // The casino / game-name field is a searchable dropdown of casino names.
    if (key === "s16bGameName") {
      return (
        <Autocomplete
          key={key}
          freeSolo
          options={CASINO_OPTIONS}
          value={v[key] || null}
          onChange={(_e, newValue) =>
            setField(i, key, typeof newValue === "string" ? newValue : "")}
          onInputChange={(_e, newValue, reason) => {
            // Ignore the on-mount "reset" event so the default isn't cleared.
            if (reason !== "reset") {
              setField(i, key, newValue ?? "")
            }
          }}
          renderInput={(params) =>
            <TextField {...params} label={label} fullWidth margin="dense" />
          }
        />
      )
    }
    return (
      <TextField
        key={key}
        label={label}
        type={type}
        fullWidth
        margin="dense"
        value={v[key]}
        onChange={(e) => setField(i, key, e.target.value)}
        InputLabelProps={type === "date" ? { shrink: true } : undefined}
      />
    )
  }

  async function persist (): Promise<boolean> {
    if (!userId || !claimId) {
      notify("Missing claim reference; cannot save.", { type: "warning" })
      return false
    }
    const updated = providers.map((provider, i) =>
      values[i] ? { ...provider, ...values[i] } : provider,
    )
    await db
      .doc(`users/${userId}/claims/${claimId}`)
      .update({ "customFieldValues.providers.value": updated })
    return true
  }

  async function save () {
    setSaving(true)
    try {
      if (await persist()) {
        notify("§16b details saved.", { type: "success" })
        handleClose()
      }
    }
    catch (err: any) {
      notify(
        err?.message ?? "Failed to save §16b details.",
        { type: "warning" },
      )
    }
    setSaving(false)
  }

  // Save, then regenerate the funder contract PDFs from the fresh data via the
  // same callable the "Generate Funder Contract" button uses.
  async function saveAndRegenerate () {
    setSaving(true)
    try {
      if (await persist()) {
        const generate = getHttpsCallable("admin-generateFunderContract")
        const res: any = await generate({ claimId, userId })
        // The funder contract does not cover the POA, so regenerate the
        // Vollmacht too (for any law firm) to reflect the updated operator.
        const generatePoa = getHttpsCallable("admin-generatePOA")
        await generatePoa({ claimId, userId })
        notify(
          res?.data?.message ?? "§16b saved and contract regenerated.",
          { type: "success", autoHideDuration: 10e3, multiLine: true },
        )
        handleClose()
      }
    }
    catch (err: any) {
      notify(
        err?.message ?? "Failed to save & regenerate.",
        { type: "warning" },
      )
    }
    setSaving(false)
  }

  return (
    <>
      <Button
        color="primary"
        variant="outlined"
        startIcon={<GavelIcon />}
        onClick={openDialog}
      >
        Review §16b
      </Button>
      <Dialog
        open={open}
        onClose={() => !saving && handleClose()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>§16b – Angaben zum Online-Glücksspiel-Account</DialogTitle>
        <DialogContent>
          {shownIndices.map((i, n) => {
            const v = values[i]
            if (!v) {
              return null
            }
            return (
              <div key={i} style={{ marginBottom: 16 }}>
                {shownIndices.length > 1 &&
                  <Typography variant="subtitle2" color="textSecondary">
                    {providers[i]?.name ?? `Provider ${i}`}
                  </Typography>
                }
                {FIELD_LABELS.map((field) => renderField(i, v, field))}
                {n < shownIndices.length - 1 &&
                  <Divider style={{ marginTop: 12 }} />
                }
              </div>
            )
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button color="primary" onClick={save} disabled={saving}>
            Save
          </Button>
          {signatureSubmitted &&
            <Button
              color="primary"
              variant="contained"
              onClick={saveAndRegenerate}
              disabled={saving}
            >
              {saving ? "Working…" : "Save & Regenerate"}
            </Button>
          }
        </DialogActions>
      </Dialog>
    </>
  )
}
